// Fiverr sub-category (the "service") id → display name.
//
// The Talent dashboard's Sub-category column carries
// `at_contact_primary_sub_category_id`, an integer from the gateway's `Gig.category`
// taxonomy object. Nothing we run maps those ids to names: the gateway sends ids
// only and exposes no taxonomy endpoint, `items_gigs.sub_category_name` has the
// column and zero rows in every database on the Cloud SQL instance, and
// `agent/data/industry_gap_profiles.json` names the eleven top-level VERTICALS, not
// these. So this is a committed seed, supplied by the owner from the Fiverr taxonomy
// on 2026-07-30, which is the "small committed seed table" the tab-1 plan called the
// cheapest honest v1 (`docs/plans/2026-07-29-talent-dashboard-tab1-plan.md`, §B2).
//
// RULES FOR EDITING:
//   - An id that is not in here renders as its raw NUMBER, never as "Unknown": we
//     know exactly which service it is and can still filter and group by it, we just
//     cannot spell it. So a stale seed degrades to today's behaviour, never to a
//     wrong label.
//   - Never add a pair from general knowledge of Fiverr's catalogue. A plausible
//     wrong name mislabels a whole column and reads exactly like a right one. New
//     pairs come from the taxonomy export, the same way these did.
//   - Every vertical has its own "Other" bucket under a DIFFERENT id, so those
//     eleven labels carry their vertical. Without it the column shows "Other" eleven
//     times over and a filter on it means nothing.
//   - The vertical comments are the grouping the export came in. They are not used
//     at runtime (the dashboard has no vertical column) but they are what makes the
//     next update diffable against a fresh export.
export const SUB_CATEGORY_NAMES: Readonly<Record<string, string>> = {
  // ── 2 · Digital Marketing ──────────────────────────────────────────────────
  "59": "Web Analytics",
  "65": "Search Engine Optimization (SEO)",
  "67": "Social Media Marketing",
  "68": "Web Traffic",
  "69": "Video Marketing",
  "120": "Other (Digital Marketing)",
  "194": "Email Marketing",
  "200": "Search Engine Marketing (SEM)",
  "206": "Local SEO",
  "212": "Influencer Marketing",
  "218": "Marketing Strategy",
  "224": "Mobile App Marketing",
  "325": "E-Commerce Marketing",
  "327": "Music Promotion",
  "352": "Crowdfunding",
  "376": "Public Relations",
  "393": "Podcast Marketing",
  "415": "Book & eBook Marketing",
  "434": "Online Communities",
  "440": "Affiliate Marketing",
  "444": "Display Advertising",
  "446": "Text Message Marketing",
  "486": "Marketing Consultation",
  "495": "Guest Posting",
  "607": "Conversion Rate Optimization (CRO)",
  "608": "Social Commerce",
  "610": "Marketing Concepts & Ideation",
  "627": "Marketing Automation",
  "628": "Generative Engine Optimization",
  "632": "Conscious Branding & Marketing",

  // ── 3 · Graphics & Design ──────────────────────────────────────────────────
  "48": "Cartoon Illustration",
  "49": "Logo Design",
  "50": "Illustration",
  "51": "Book Design",
  "53": "Image Editing",
  "54": "Presentation Design",
  "55": "Flyer Design",
  "56": "Business Cards & Stationery",
  "122": "Other (Graphics & Design)",
  "148": "Web Banners",
  "149": "Social Media Design",
  "150": "Architecture & Interior Design",
  "151": "Website Design",
  "153": "Invitation Design",
  "154": "T-Shirts & Merchandise",
  "155": "Vector Tracing",
  "246": "Infographic Design",
  "331": "Packaging & Label Design",
  "333": "Industrial & Product Design",
  "340": "Portraits & Caricatures",
  "345": "Car Wraps",
  "358": "Brochure Design",
  "359": "Poster Design",
  "360": "Menu Design",
  "362": "Catalog Design",
  "363": "Brand Style Guides",
  "365": "Game Art",
  "366": "Character Modeling",
  "367": "Storyboards",
  "371": "Graphics for Streamers",
  "378": "Building Information Modeling",
  "379": "Landscape Design",
  "385": "Tattoo Design",
  "386": "Podcast Cover Art",
  "411": "Album Cover Design",
  "419": "Trade Booth Design",
  "420": "Pattern Design",
  "422": "Resume Design",
  "429": "Signage Design",
  "441": "Fashion Design",
  "447": "Jewelry Design",
  "460": "Email Design",
  "462": "Icon Design",
  "466": "App Design",
  "467": "UX Design",
  "468": "Landing Page Design",
  "479": "Building Engineering",
  "480": "NFT Art",
  "485": "Fonts & Typography",
  "488": "Design Consultation",
  "526": "AI Artists",
  "536": "Children's Book Illustration",
  "573": "NSFW Art",
  "601": "AI Avatar Design",
  "621": "Lighting Design",
  "637": "Art Direction",
  "638": "Comic Illustration",

  // ── 5 · Writing & Translation ──────────────────────────────────────────────
  "107": "Creative Writing",
  "108": "Translation",
  "109": "Transcription",
  "112": "Resume Writing",
  "114": "Proofreading & Editing",
  "115": "Press Releases",
  "121": "Other (Writing & Translation)",
  "163": "Articles & Blog Posts",
  "169": "Research & Summaries",
  "334": "Product Descriptions",
  "335": "Website Content",
  "336": "Sales Copy",
  "337": "Email Copy",
  "338": "Business Names & Slogans",
  "341": "Technical Writing",
  "347": "Beta Reading",
  "353": "Scriptwriting",
  "354": "Book & eBook Writing",
  "355": "Speechwriting",
  "381": "UX Writing",
  "382": "White Papers",
  "388": "Case Studies",
  "389": "Podcast Writing",
  "395": "Social Media Copywriting",
  "396": "Brand Voice & Tone",
  "397": "Cover Letters",
  "398": "LinkedIn Profiles",
  "404": "Grant Writing",
  "430": "Book Editing",
  "435": "Ad Copy",
  "436": "eLearning Content Development",
  "470": "Job Descriptions",
  "490": "Writing Advice",
  "538": "Localization",
  "539": "Content Strategy",
  "550": "Custom Writing Prompts",
  "609": "Interpretation",
  "629": "Handwriting",
  "636": "Academic Support",

  // ── 7 · Personal Growth & Hobbies ──────────────────────────────────────────
  "73": "Life Coaching",
  "75": "Wellness",
  "77": "Online Tutoring",
  "78": "Astrology & Psychics",
  "123": "Other (Personal Growth & Hobbies)",
  "127": "Traveling",
  "279": "Competitive Gaming",
  "281": "Collectibles",
  "287": "Family & Genealogy",
  "310": "Arts & Crafts",
  "321": "Greeting Cards & Videos",
  "431": "Styling & Beauty",
  "458": "Modeling & Acting",
  "500": "Puzzle & Game Creation",
  "517": "Career Counseling",
  "522": "Fitness",
  "523": "Nutrition",
  "524": "Cosmetics Formulation",
  "531": "Trend Forecasting",
  "544": "Language Lessons",
  "567": "Embroidery Digitizing",
  "595": "Recreational Gaming",
  "596": "Game Testing & Feedback",
  "597": "Ingame Creation",
  "600": "Game Recordings & Guides",
  "604": "Cosplay Creation",
  "630": "Tabletop Games",

  // ── 8 · Business ───────────────────────────────────────────────────────────
  "21": "Business Plans",
  "23": "Market Research",
  "24": "Presentations",
  "25": "Virtual Assistant",
  "26": "Business Consulting",
  "30": "Legal Consulting",
  "31": "Other (Business)",
  "351": "Lead Generation",
  "408": "Project Management",
  "409": "HR Consulting",
  "423": "E-Commerce Management",
  "442": "Supply Chain Management",
  "443": "Game Concept Design",
  "449": "Customer Service",
  "469": "Sales",
  "475": "Event Management",
  "566": "AI Consulting",
  "568": "Product Management",
  "598": "Online Investigations",
  "602": "Sustainability Consulting",
  "603": "Software Management",
  "622": "Business Formation & Registration",
  "623": "Intellectual Property Management",
  "631": "Customer Experience Management (CXM)",
  "634": "Regulatory Compliance Consulting",

  // ── 10 · Programming & Tech ────────────────────────────────────────────────
  "97": "Other (Programming & Tech)",
  "140": "Software Development",
  "141": "Mobile App Development",
  "144": "User Testing",
  "145": "QA & Review",
  "146": "Support & IT",
  "147": "Convert Files",
  "328": "Chatbot Development",
  "364": "Game Development",
  "394": "Cybersecurity",
  "416": "Online Coding Lessons",
  "425": "Development for Streamers",
  "464": "Blockchain Development & Solutions",
  "477": "Electronics Engineering",
  "483": "NFT Development",
  "501": "Cloud Computing",
  "514": "Website Development",
  "515": "Website Maintenance",
  "520": "AI Development",
  "533": "Trading Bots Development",
  "574": "Mobile App Maintenance",
  "593": "Wearable App Development",
  "599": "DevOps Engineering",
  "605": "Cryptocurrencies & Tokens",
  "639": "Vibe Coding",

  // ── 12 · Music & Audio ─────────────────────────────────────────────────────
  "10": "Mixing & Mastering",
  "11": "Jingles & Intros",
  "16": "Voice Over",
  "17": "Sound Design",
  "20": "Other (Music & Audio)",
  "39": "Custom Songs",
  "156": "Session Musicians",
  "157": "Music Producers",
  "158": "Singers & Vocalists",
  "346": "Audio Editing",
  "349": "Vocal Tuning",
  "380": "Music Transcription",
  "390": "Podcast Production",
  "391": "Audiobook Production",
  "392": "Songwriting",
  "405": "DJ Drops & Tags",
  "406": "Audio Ads Production",
  "412": "Online Music Lessons",
  "437": "DJ Mixing",
  "463": "Meditation Music",
  "478": "Audio Logo & Sonic Branding",
  "489": "Music & Audio Consultation",
  "535": "Voice Synthesis & AI",
  "591": "Composers",
  "592": "Custom Patches & Samples",
  "606": "Audio Plugin Development",

  // ── 20 · Video & Animation ─────────────────────────────────────────────────
  "99": "Video Editing",
  "101": "Spokespersons Videos",
  "104": "Logo Animation",
  "117": "Other (Video & Animation)",
  "228": "Animated Explainers",
  "234": "Music Videos",
  "235": "Character Animation",
  "249": "Slideshow Videos",
  "329": "Video Ads & Commercials",
  "330": "Live Action Explainers",
  "343": "Intro & Outro Videos",
  "344": "Visual Effects",
  "350": "Animation for Kids",
  "356": "Animated GIFs",
  "368": "Animation for Streamers",
  "369": "3D Product Animation",
  "372": "Game Trailers",
  "384": "Subtitles & Captions",
  "399": "App & Website Previews",
  "400": "Book Trailers",
  "401": "Real Estate Promos",
  "402": "Screencasting Videos",
  "421": "Article to Video",
  "426": "eLearning Video Production",
  "439": "Drone Videography",
  "445": "Lottie & Web Animation",
  "459": "E-Commerce Product Videos",
  "472": "Crowdfunding Videos",
  "473": "Corporate Videos",
  "474": "Video Templates Editing",
  "476": "Meditation Videos",
  "482": "Social Media Videos",
  "484": "NFT Animation",
  "487": "Video Consultation",
  "497": "Videographers",
  "499": "Filmed Video Production",
  "518": "Rigging",
  "545": "AI Videography",
  "549": "UGC Videos",
  "562": "Video Art Creation",
  "582": "Text Animation",
  "612": "Virtual & Streaming Avatars",
  "625": "Video Repurposing",

  // ── 23 · Data ──────────────────────────────────────────────────────────────
  "451": "Databases",
  "452": "Data Processing",
  "453": "Data Analytics",
  "454": "Data Visualization",
  "455": "Data Science & ML",
  "456": "Data Entry",
  "457": "Other (Data)",
  "471": "Data Engineering",
  "540": "Data Formatting",
  "541": "Data Labeling & Annotation",
  "542": "Data Scraping",
  "543": "Data Governance & Protection",
  "584": "Data Cleaning",
  "585": "Data Enrichment",

  // ── 25 · Photography ───────────────────────────────────────────────────────
  "503": "Product Photographers",
  "504": "Portrait Photographers",
  "505": "Lifestyle & Fashion Photographers",
  "506": "Real Estate Photographers",
  "507": "Event Photographers",
  "508": "Food Photographers",
  "510": "Photography Consultation",
  "511": "Other (Photography)",
  "551": "Scenic Photographers",
  "578": "Photo Preset Creation",
  "640": "Drone Photography",

  // ── 29 · Finance ───────────────────────────────────────────────────────────
  "614": "Financial Planning & Analysis",
  "615": "Corporate Finance",
  "616": "Fundraising",
  "617": "Accounting",
  "618": "Tax Consulting",
  "619": "Personal Finance",
  "620": "Other (Finance)",
  "633": "Banking",
  "635": "Investments",
};

/** The name for a sub-category id, falling back to the id itself.
 *
 *  A null id is UNKNOWN and returns null so the caller renders its "Unknown" badge.
 *  An id we hold no name for returns the ID, never "Unknown": we know exactly which
 *  service it is and can still filter and group by it, we just cannot spell it. */
export function subCategoryLabel(id: number | null | undefined): string | null {
  // `undefined` as well as `null`: the generated wire types make every defaulted
  // field optional, so an absent sub-category arrives as undefined. Both mean the
  // same thing here — we do not know which service this was — and separating them
  // would only push a `?? null` onto every call site.
  if (id == null) return null;
  return SUB_CATEGORY_NAMES[String(id)] ?? String(id);
}
